import React, { useState, useEffect } from 'react';
import { Trophy, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { leagueAPI } from '../services/api';

const TournamentBracket = ({ seasonId, isAdmin = false }) => {
    const [matches, setMatches] = useState([]);
    const [groups, setGroups] = useState([]);
    const [showSourceModal, setShowSourceModal] = useState(false);
    const [selectedMatch, setSelectedMatch] = useState(null);
    const [sourceForm, setSourceForm] = useState({
        team_a_source: 'none',
        team_b_source: 'none'
    });

    useEffect(() => {
        if (seasonId) {
            loadData();
        }
    }, [seasonId]);

    const loadData = async () => {
        if (!seasonId) return;

        try {
            const [matchesRes, groupsRes] = await Promise.all([
                leagueAPI.getMatches(seasonId, null),
                leagueAPI.getGroups(seasonId)
            ]);

            // Filter bracket matches (those without group_id)
            const bracketMatches = (matchesRes.data || []).filter(m => !m.group_id);
            setMatches(bracketMatches);
            setGroups(groupsRes.data || []);
        } catch (err) {
            console.error('Failed to load bracket data:', err);
            setMatches([]);
            setGroups([]);
        }
    };

    const handleSetSource = (match) => {
        setSelectedMatch(match);
        setSourceForm({
            team_a_source: match.team_a_source || 'none',
            team_b_source: match.team_b_source || 'none'
        });
        setShowSourceModal(true);
    };

    const handleSaveSource = async () => {
        if (!selectedMatch) return;

        try {
            await leagueAPI.updateMatch(selectedMatch.id, {
                team_a_source: sourceForm.team_a_source === 'none' ? null : (sourceForm.team_a_source || null),
                team_b_source: sourceForm.team_b_source === 'none' ? null : (sourceForm.team_b_source || null)
            });
            setShowSourceModal(false);
            loadData();
        } catch (err) {
            console.error('Failed to update source:', err);
        }
    };

    const renderTeamSlot = (match, side) => {
        const source = side === 'a' ? match.team_a_source : match.team_b_source;
        const team = side === 'a' ? match.team_a : match.team_b;

        if (team && team.id) {
            return <span className="text-xs font-medium text-white">{team.name}</span>;
        }

        const customName = side === 'a' ? match.team_a_custom_name : match.team_b_custom_name;
        if (customName) {
            return <span className="text-xs font-medium text-white">{customName}</span>;
        }

        if (source && source.startsWith('group:')) {
            const parts = source.split(':');
            if (parts.length === 3) {
                const groupId = parts[1];
                const rank = parts[2];
                const group = groups.find(g => g.id === groupId);
                const groupName = group ? group.name : 'Group';
                return <span className="text-xs text-blue-400">{groupName} #{rank}</span>;
            }
        }

        return <span className="text-xs text-gray-500">TBD</span>;
    };

    const renderMatch = (match, isRightSide = false) => {
        const hasScore = match.score_a !== null && match.score_b !== null;
        const winner = hasScore ? (match.score_a > match.score_b ? 'a' : 'b') : null;

        return (
            <div key={match.id} className={`flex flex-col justify-center my-4 relative z-10 ${isRightSide ? 'items-end' : 'items-start'}`}>
                <div className={`
                    w-44 bg-[#0a0a0b] border transition-all duration-300 rounded-lg overflow-hidden shadow-2xl
                    ${hasScore ? 'border-gray-800' : 'border-blue-500/30'}
                    hover:border-blue-500 hover:shadow-blue-500/10 group
                `}>
                    {/* Header: Round & Admin */}
                    <div className="bg-[#111112] px-3 py-1.5 flex justify-between items-center border-b border-gray-800/50">
                        <span className="text-[9px] font-black uppercase tracking-widest text-gray-500">
                            Match {match.match_number || '?'}
                        </span>
                        {isAdmin && (
                            <Settings
                                className="w-3 h-3 text-gray-600 hover:text-blue-400 cursor-pointer transition-colors"
                                onClick={() => handleSetSource(match)}
                            />
                        )}
                    </div>

                    {/* Team A */}
                    <div className={`px-3 py-2 flex justify-between items-center ${winner === 'a' ? 'bg-blue-500/5' : ''}`}>
                        <div className={`flex items-center gap-2 overflow-hidden transition-opacity duration-500 ${winner === 'b' ? 'opacity-40 grayscale-[0.5]' : ''}`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${winner === 'a' ? 'bg-blue-500' : 'bg-gray-700'}`} />
                            <div className="truncate">
                                {renderTeamSlot(match, 'a')}
                            </div>
                        </div>
                        <span className={`text-xs font-mono font-black tabular-nums transition-colors ${winner === 'a' ? 'text-blue-400' : 'text-gray-600'}`}>
                            {match.score_a !== null ? match.score_a : '-'}
                        </span>
                    </div>

                    {/* Team B */}
                    <div className={`px-3 py-2 flex justify-between items-center border-t border-gray-800/30 ${winner === 'b' ? 'bg-blue-500/5' : ''}`}>
                        <div className={`flex items-center gap-2 overflow-hidden transition-opacity duration-500 ${winner === 'a' ? 'opacity-40 grayscale-[0.5]' : ''}`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${winner === 'b' ? 'bg-blue-500' : 'bg-gray-700'}`} />
                            <div className="truncate">
                                {renderTeamSlot(match, 'b')}
                            </div>
                        </div>
                        <span className={`text-xs font-mono font-black tabular-nums transition-colors ${winner === 'b' ? 'text-blue-400' : 'text-gray-600'}`}>
                            {match.score_b !== null ? match.score_b : '-'}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    const renderBracket = () => {
        if (matches.length === 0) {
            return (
                <div className="text-center py-20 bg-[#1a1a1b]/30 border border-dashed border-gray-800 rounded-2xl">
                    <div className="w-20 h-20 bg-gray-800/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Trophy className="w-10 h-10 text-gray-700" />
                    </div>
                    <p className="text-gray-400 font-medium">Turnuva ağacı henüz oluşturulmadı.</p>
                    {isAdmin && <p className="text-xs text-gray-600 mt-2 uppercase tracking-widest font-black">Admin panelinden maçları oluşturun</p>}
                </div>
            );
        }

        const rounds = {};
        matches.forEach(m => {
            const r = m.round || 1;
            if (!rounds[r]) rounds[r] = [];
            rounds[r].push(m);
        });

        const roundNums = Object.keys(rounds).map(Number).sort((a, b) => a - b);
        const maxRound = Math.max(...roundNums);

        const leftSide = [];
        const rightSide = [];
        let centerMatch = null;

        roundNums.forEach(r => {
            const roundMatches = [...rounds[r]].sort((a, b) => (a.match_number || 0) - (b.match_number || 0));
            if (r === maxRound) {
                centerMatch = roundMatches[0];
            } else {
                const mid = Math.ceil(roundMatches.length / 2);
                leftSide.push({ round: r, matches: roundMatches.slice(0, mid) });
                rightSide.push({ round: r, matches: roundMatches.slice(mid) });
            }
        });

        const getRoundLabel = (r, total) => {
            if (r === total) return "Final";
            if (r === total - 1) return "Semi Finals";
            if (r === total - 2) return "Quarter Finals";
            return `Round ${r}`;
        };

        return (
            <div className="relative flex items-center justify-center gap-4 lg:gap-16 min-w-max px-8 py-20 select-none overflow-visible">
                {/* Left Side Rounds */}
                <div className="flex items-center gap-8 lg:gap-16">
                    {leftSide.map((rd, i) => (
                        <div key={`left-${rd.round}`} className="flex flex-col gap-12 relative">
                            <div className="absolute -top-10 left-0 right-0 text-center">
                                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-500/50">
                                    {getRoundLabel(rd.round, maxRound)}
                                </span>
                            </div>
                            <div className="flex flex-col justify-around h-full gap-8">
                                {rd.matches.map((m, idx) => (
                                    <div key={m.id} className="relative">
                                        {renderMatch(m, false)}
                                        {/* Connector to next round */}
                                        {rd.round < maxRound && (
                                            <div className={`
                                                absolute -right-8 lg:-right-16 top-1/2 -translate-y-1/2
                                                w-8 lg:w-16 h-px bg-gradient-to-r from-gray-800 to-blue-500/30
                                            `} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Center: Grand Final */}
                {centerMatch && (
                    <div className="flex flex-col items-center gap-12 relative px-4">
                        <div className="absolute -top-16 left-1/2 -translate-x-1/2 text-center w-64">
                            <div className="relative inline-block mb-2">
                                <Trophy className="w-10 h-10 text-yellow-500 mx-auto drop-shadow-[0_0_15px_rgba(234,179,8,0.5)] animate-bounce" />
                                <div className="absolute -inset-4 bg-yellow-400/10 blur-2xl rounded-full animate-pulse -z-10" />
                            </div>
                            <h4 className="text-white text-xs font-black uppercase tracking-[0.6em] drop-shadow-lg">
                                GRAND FINAL
                            </h4>
                        </div>
                        <div className="flex flex-col justify-center h-full pt-8">
                            <div className="scale-110 transform transition-all duration-700 hover:scale-125 relative">
                                <div className="absolute -inset-6 bg-blue-500/5 blur-3xl rounded-full animate-pulse z-0" />
                                <div className="relative z-10">
                                    {renderMatch(centerMatch, false)}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Right Side Rounds (Reversed) */}
                <div className="flex items-center gap-8 lg:gap-16">
                    {[...rightSide].reverse().map((rd, i) => (
                        <div key={`right-${rd.round}`} className="flex flex-col gap-12 relative">
                            <div className="absolute -top-10 left-0 right-0 text-center">
                                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-500/50">
                                    {getRoundLabel(rd.round, maxRound)}
                                </span>
                            </div>
                            <div className="flex flex-col justify-around h-full gap-8">
                                {rd.matches.map(m => (
                                    <div key={m.id} className="relative">
                                        {/* Connector from previous (outer) round */}
                                        {rd.round < maxRound && (
                                            <div className={`
                                                absolute -left-8 lg:-left-16 top-1/2 -translate-y-1/2
                                                w-8 lg:w-16 h-px bg-gradient-to-l from-gray-800 to-blue-500/30
                                            `} />
                                        )}
                                        {renderMatch(m, true)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="w-full bg-[#050506] rounded-3xl border border-gray-900 shadow-inner overflow-hidden p-4">
            <div className="overflow-x-auto custom-scrollbar">
                {renderBracket()}
            </div>

            {/* Source Configuration Modal */}
            <Dialog open={showSourceModal} onOpenChange={setShowSourceModal}>
                <DialogContent className="bg-[#0a0a0b] border border-gray-800 text-white shadow-2xl backdrop-blur-xl">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-black uppercase tracking-widest text-blue-500">Configure Match Source</DialogTitle>
                    </DialogHeader>
                    {/* ... rest of modal is same ... */}
                    <div className="space-y-4">
                        <div>
                            <Label className="text-gray-300">Team A Source</Label>
                            <Select
                                value={sourceForm.team_a_source}
                                onValueChange={v => setSourceForm({ ...sourceForm, team_a_source: v })}
                            >
                                <SelectTrigger className="bg-[#0a0a0b] text-white border-gray-700">
                                    <SelectValue placeholder="Select source" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#1a1a1b] border-gray-700 text-white">
                                    <SelectItem value="none">None (Manual)</SelectItem>
                                    {groups.map(g => (
                                        <React.Fragment key={g.id}>
                                            <SelectItem value={`group:${g.id}:1`}>{g.name} - 1st Place</SelectItem>
                                            <SelectItem value={`group:${g.id}:2`}>{g.name} - 2nd Place</SelectItem>
                                        </React.Fragment>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="text-gray-300">Team B Source</Label>
                            <Select
                                value={sourceForm.team_b_source}
                                onValueChange={v => setSourceForm({ ...sourceForm, team_b_source: v })}
                            >
                                <SelectTrigger className="bg-[#0a0a0b] text-white border-gray-700">
                                    <SelectValue placeholder="Select source" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#1a1a1b] border-gray-700 text-white">
                                    <SelectItem value="none">None (Manual)</SelectItem>
                                    {groups.map(g => (
                                        <React.Fragment key={g.id}>
                                            <SelectItem value={`group:${g.id}:1`}>{g.name} - 1st Place</SelectItem>
                                            <SelectItem value={`group:${g.id}:2`}>{g.name} - 2nd Place</SelectItem>
                                        </React.Fragment>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <Button
                            onClick={handleSaveSource}
                            className="w-full bg-blue-600 hover:bg-blue-700"
                        >
                            Save Source Configuration
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TournamentBracket;
